
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';

const setupFormSchema = z.object({
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type SetupFormValues = z.infer<typeof setupFormSchema>;

const SetupAccount: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [clientData, setClientData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<'valid' | 'invalid' | 'expired' | 'used'>('valid');

  const token = searchParams.get('token');

  const form = useForm<SetupFormValues>({
    resolver: zodResolver(setupFormSchema),
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenStatus('invalid');
        setIsLoading(false);
        return;
      }

      try {
        // Fetch client data using the token
        const { data: client, error } = await supabase
          .from('client_onboarding')
          .select('*')
          .eq('invitation_token', token)
          .single();

        if (error || !client) {
          console.error('Token validation error:', error);
          setTokenStatus('invalid');
          setIsLoading(false);
          return;
        }

        // Check if account setup is already completed
        if (client.account_setup_completed_at) {
          setTokenStatus('used');
          setIsLoading(false);
          return;
        }

        // Check if token is expired
        if (client.invitation_expires_at && new Date(client.invitation_expires_at) < new Date()) {
          setTokenStatus('expired');
          setIsLoading(false);
          return;
        }

        setClientData(client);
        setTokenStatus('valid');
      } catch (error) {
        console.error('Error validating token:', error);
        setTokenStatus('invalid');
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const onSubmit = async (data: SetupFormValues) => {
    if (!clientData || !token) return;

    setIsSubmitting(true);

    try {
      // Create auth user account
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: clientData.email,
        password: data.password,
        options: {
          data: {
            full_name: clientData.full_name,
            company_name: clientData.company_name
          },
          emailRedirectTo: `${window.location.origin}/dashboard`
        }
      });

      if (signUpError) {
        console.error('Sign up error:', signUpError);
        toast({
          title: "Account Creation Failed",
          description: signUpError.message,
          variant: "destructive",
        });
        return;
      }

      if (!authData.user) {
        toast({
          title: "Account Creation Failed",
          description: "Failed to create user account. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Update client_onboarding record with the new user ID and mark setup as complete
      const { error: updateError } = await supabase
        .from('client_onboarding')
        .update({
          id: authData.user.id, // Link to auth.users
          account_setup_completed_at: new Date().toISOString(),
          invitation_token: null, // Clear the token
          status: 'verified' // Ensure status is verified
        })
        .eq('invitation_token', token);

      if (updateError) {
        console.error('Error updating client record:', updateError);
        // Note: User account is created but linking failed - this needs manual intervention
        toast({
          title: "Account Created",
          description: "Your account was created but there was an issue linking it to your client record. Please contact support.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Account Setup Complete!",
        description: "Your account has been successfully created. You can now access your dashboard.",
      });

      // Redirect to dashboard after brief delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (error: any) {
      console.error('Account setup error:', error);
      toast({
        title: "Setup Failed",
        description: "An unexpected error occurred. Please try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <SEO 
          title="Account Setup - IT Management Solutions"
          description="Complete your account setup to access your client portal."
        />
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p>Validating your invitation...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const renderContent = () => {
    switch (tokenStatus) {
      case 'invalid':
        return (
          <Card className="w-full max-w-md mx-auto">
            <CardHeader className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <CardTitle className="text-red-600">Invalid Invitation</CardTitle>
              <CardDescription>
                This invitation link is not valid. Please check the URL or contact support for assistance.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => navigate('/')} 
                className="w-full"
              >
                Return to Home
              </Button>
            </CardContent>
          </Card>
        );

      case 'expired':
        return (
          <Card className="w-full max-w-md mx-auto">
            <CardHeader className="text-center">
              <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <CardTitle className="text-orange-600">Invitation Expired</CardTitle>
              <CardDescription>
                This invitation link has expired. Please contact support to request a new invitation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => navigate('/contact')} 
                className="w-full mb-2"
              >
                Contact Support
              </Button>
              <Button 
                onClick={() => navigate('/')} 
                variant="outline"
                className="w-full"
              >
                Return to Home
              </Button>
            </CardContent>
          </Card>
        );

      case 'used':
        return (
          <Card className="w-full max-w-md mx-auto">
            <CardHeader className="text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <CardTitle className="text-green-600">Account Already Setup</CardTitle>
              <CardDescription>
                Your account has already been set up. You can sign in to access your dashboard.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => navigate('/login')} 
                className="w-full mb-2"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/')} 
                variant="outline"
                className="w-full"
              >
                Return to Home
              </Button>
            </CardContent>
          </Card>
        );

      case 'valid':
        return (
          <Card className="w-full max-w-md mx-auto">
            <CardHeader className="text-center">
              <CardTitle>Complete Your Account Setup</CardTitle>
              <CardDescription>
                Welcome {clientData?.full_name}! Set your password to access your client portal.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {clientData && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">Account Information</h3>
                  <p className="text-sm text-gray-600"><strong>Name:</strong> {clientData.full_name}</p>
                  <p className="text-sm text-gray-600"><strong>Email:</strong> {clientData.email}</p>
                  {clientData.company_name && (
                    <p className="text-sm text-gray-600"><strong>Company:</strong> {clientData.company_name}</p>
                  )}
                </div>
              )}

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input 
                            type="password" 
                            placeholder="Enter your password" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm Password</FormLabel>
                        <FormControl>
                          <Input 
                            type="password" 
                            placeholder="Confirm your password" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="text-xs text-gray-500 mb-4">
                    Password requirements:
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>At least 8 characters long</li>
                      <li>Contains uppercase and lowercase letters</li>
                      <li>Contains at least one number</li>
                    </ul>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Setting up account...
                      </>
                    ) : (
                      'Complete Setup'
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title="Account Setup - IT Management Solutions"
        description="Complete your account setup to access your client portal."
      />
      <Navbar />
      
      <section className="flex-1 bg-gray-50 py-12 md:py-16">
        <div className="container mx-auto px-4">
          {renderContent()}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SetupAccount;
